// @flow

import isPlainObject from 'lodash/isPlainObject';
import type { LogDataType } from 'app/common/redux/lib/reportError';

/**
 * A simple rate limiter based on the key.
 * Constructs a LogDataRateLimiter instance with a parameter interval
 * Interval - number in millisecond. Duplicate calls within the rate limit will return
 * false.
 */
export default class LogDataRateLimiter {
  table: {
    [string]: number,
  };
  interval: number;
  lastCallTime: number;

  constructor(interval: number) {
    this.table = {};
    this.interval = interval;
    this.lastCallTime = Date.now();
  }

  /**
   * Garbage collect the entries in the table if the last call time to this rate limiter is greater
   * than its interval.
   */
  _garbageCollectEntries() {
    if (Date.now() - this.lastCallTime > this.interval) {
      this.table = {};
    }
  }

  /**
   * Hash the object based on its sorted keys by combining its key:value pairs.
   * Assumption: We're not logging giant string of log.
   */
  _generateHash(obj: Object): string {
    const sortedObjKeys = Object.keys(obj).sort();
    const values = [];
    sortedObjKeys.forEach((key) => {
      // null/undefined check
      if (obj[key]) {
        let value = isPlainObject(obj[key]) ? JSON.stringify(obj[key]) : obj[key] + ''; // convert to string
        value = key + ':' + value;
        values.push(value);
      }
    });
    return values.join('');
  }

  getTableKey(logData: LogDataType): string {
    if (!logData.auxData) {
      // Even though message might be empty, it's the best property we can use as key
      // because - other properties are probably empty if the message is empty -
      // other properties are not unique.
      return logData.errorObj.message ? logData.errorObj.message : '';
    }

    return this._generateHash(
      Object.assign({ message: logData.errorObj.message }, logData.auxData),
    );
  }

  isWithinRateLimit(logData: LogDataType) {
    this._garbageCollectEntries();
    const now = Date.now();
    this.lastCallTime = now;
    const key = this.getTableKey(logData);
    if (!this.table[key]) {
      this.table[key] = now;
      return false;
    } else {
      if (now - this.table[key] < this.interval) {
        return true;
      }
      this.table[key] = now;
      return false;
    }
  }
}
